import { Input, Component, OnInit } from '@angular/core';
import { SubjectId } from '../../../resources/config';
import { environment } from '../../../../environments/environment';
import { ProblemImageUtil } from '../../../utils/problem-image-util';
import { CommonIdSubjectUtil } from '../../../utils/common-id/common-id-subject-util';

@Component({
  selector: 'app-thumb-preview-problem',
  templateUrl: './thumb-preview-problem.component.html',
  styleUrls: ['./thumb-preview-problem.component.scss']
})
export class ThumbPreviewProblemComponent implements OnInit {
  /**
   * isPremiumMemberOnly: プレミアムメンバー限定の問題を隠して表示するかどうか
   */
  @Input() isPremiumMemberOnly: boolean;
  /**
   * ratio: 0〜1の範囲で指定する
   */
  @Input() ratio: number;
  /**
   * 大問ID
   */
  @Input() problemId: string;
  /**
   *
   */
  @Input() hasExternalData: boolean | undefined;
  /**
   * 画像のsrc
   */
  imageSrc: string;
  imageRatio: number;
  /**
   * 画像の水平方向開始位置
   */
  startPosX: 'left' | 'right' = 'left';

  /**
   * 画像サイズの基準にする方向
   */
  baseSizeDir: 'width' | 'height';

  /**
   * サムネイル画像ファイル名のうち _l{layoutType} / @2x を除いた先頭部分（例: 2350...._q01_01）
   */
  private thumbFileNamePrefix: string;
  /**
   * Retina 用サフィックス（@2x もしくは空文字）
   */
  private retinaSuffix = '';
  /**
   * 試行する layoutType サフィックス候補。推薦・総合は大問ごとにレイアウトが混在し
   * ファイル名末尾に _l{layoutType} が付くが、その layoutType は検索結果に含まれないため
   * 候補を順に試して最初に表示できたものを採用する。混在しない科目は '' のみ。
   */
  private layoutSuffixCandidates: string[] = [];
  /**
   * 現在試行中の layoutSuffixCandidates のインデックス
   */
  private layoutSuffixIndex = 0;

  constructor() {}

  ngOnInit() {
    const year = this.problemId.slice(0, 2);
    const subjectId = this.problemId.slice(6, 8);
    if (this.hasExternalData === undefined || CommonIdSubjectUtil.isExcludeYear(subjectId, year)) {
      this.imageSrc = '/assets/images/preview-problem-thumb-expired@2x.png';
      this.imageRatio = 1;
    } else if (this.isPremiumMemberOnly) {
      this.imageSrc = '/assets/images/preview-problem-thumb-premium-only@2x.png';
      this.imageRatio = 1;
    } else {
      // 23年度以降は Retina 対応されているため
      const isRetina = parseInt(year, 10) > 22 ? true : false;
      const paperId = this.problemId.slice(0, 10);
      const daimonId = this.problemId.slice(10, 12);
      this.retinaSuffix = isRetina ? '@2x' : '';
      this.thumbFileNamePrefix = `${paperId}_q${daimonId}_01`;
      // 推薦・総合(SPECIAL_ADMISSIONS)は 1 paper 内に複数レイアウトが混在し得る大問のみ、画像ファイル名の
      // 末尾に _l{layoutType}(1〜3) が付く。大半の大問は _l 無しのため、まず _l 無しを試し、404 のときだけ
      // (error) で _l1 → _l2 → _l3 へ順にフォールバックする。layoutType は大問ごとに異なり検索結果には
      // 含まれないため候補を総当たりする。他科目は _l が付かないので _l 無しの 1 候補のみ。
      this.layoutSuffixCandidates = subjectId === SubjectId.SPECIAL_ADMISSIONS ? ['', '_l1', '_l2', '_l3'] : [''];
      this.layoutSuffixIndex = 0;
      this.imageSrc = this.buildImageSrc();
      if (subjectId === SubjectId.JAPANESE_HISTORY) {
        this.imageRatio = isRetina ? this.ratio * 2 : this.ratio;
      } else {
        this.imageRatio = this.ratio;
      }
    }

    // 国語のみ右寄せで表示
    if (this.problemId.slice(6, 8) === SubjectId.NATIONAL_LANGUAGE) {
      this.startPosX = 'right';
      this.baseSizeDir = 'height';
    } else {
      this.startPosX = 'left';
      this.baseSizeDir = 'width';
    }
  }

  /**
   * 現在の layoutType 候補でサムネイル画像の src を組み立てる。
   */
  private buildImageSrc(): string {
    const layoutSuffix = this.layoutSuffixCandidates[this.layoutSuffixIndex] ?? '';
    const fileName = `${this.thumbFileNamePrefix}${layoutSuffix}${this.retinaSuffix}.gif`;
    return environment.cloudfrontHost + ProblemImageUtil.getPath(fileName, false);
  }

  /**
   * サムネイル画像の読み込みに失敗したとき、次の layoutType 候補があれば src を差し替えて再試行する。
   * 推薦・総合(混在レイアウト)で大問ごとの _l{layoutType} を特定するためのフォールバック。
   * 候補を試し切った場合は何もしない（それ以上のリクエストを発生させない）。
   */
  onImageError() {
    if (this.layoutSuffixIndex < this.layoutSuffixCandidates.length - 1) {
      this.layoutSuffixIndex++;
      this.imageSrc = this.buildImageSrc();
    }
  }
}
